import React, { useState, useRef, createElement, Component } from 'react';
import { useClickOutside } from '../../../../app/hooks/useClickOutside';
import { AlertTriangle, XCircle, CheckCircle2, Info, Search, ChevronDown, ChevronRight, Download, Hash, ShieldAlert } from 'lucide-react';
import { Badge } from '../../../../components/ui/Badge';
import { Card } from '../../../../components/ui/Card';
import { Input } from '../../../../components/ui/Input';
import { Select } from '../../../../components/ui/Select';
import type { PipelineBatchResult, GateVerdict, ValidationIssue, IssueDomain } from '../../../../governance';
import { ADMIN_MODULE_META } from '../../shared/moduleMetadata';
// ─── Constants ────────────────────────────────────────────────────────────────
export const MODULE_CONFIG = ADMIN_MODULE_META;
export const PIPELINE_STAGES: {
  name: string;
  label: string;
  domain: IssueDomain;
}[] = [{
  name: 'schemaStage',
  label: 'Schema Validatie',
  domain: 'schema'
}, {
  name: 'typeStage',
  label: 'Vraagtype Validatie',
  domain: 'type'
}, {
  name: 'contentStage',
  label: 'Inhoud & Taal',
  domain: 'content'
}, {
  name: 'supportStage',
  label: 'Hints & Uitleg',
  domain: 'support'
}, {
  name: 'visualStage',
  label: 'Visuele Hulpmiddelen',
  domain: 'visual'
}, {
  name: 'graphStage',
  label: 'LDG-Graaf',
  domain: 'graph'
}, {
  name: 'seedStage',
  label: 'Seed Integriteit',
  domain: 'seed'
}];
export const SEVERITY_CONFIG = {
  blocker: {
    icon: XCircle,
    bg: 'bg-red-50',
    text: 'text-red-700',
    border: 'border-red-200',
    label: 'Blocker'
  },
  coach: {
    icon: AlertTriangle,
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    border: 'border-amber-200',
    label: 'Verbeterpunt'
  },
  info: {
    icon: Info,
    bg: 'bg-blue-50',
    text: 'text-blue-700',
    border: 'border-blue-200',
    label: 'Info'
  }
};
// ─── Types ────────────────────────────────────────────────────────────────────
export interface LogEntry {
  time: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error';
}
// ─── Shared Components ────────────────────────────────────────────────────────
export function EmptyValidationState() {
  return <div className="py-16 flex flex-col items-center justify-center space-y-4 bg-white rounded-xl border border-gray-200 shadow-sm" data-id="element-3115">
      <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center border border-gray-100" data-id="element-3116">
        <ShieldAlert className="w-8 h-8 text-gray-300" data-id="element-3117" />
      </div>
      <div className="text-center" data-id="element-3118">
        <h2 className="text-base font-bold text-gray-900" data-id="element-3119">
          Nog geen validatie uitgevoerd
        </h2>
        <p className="text-sm text-gray-500 mt-1 max-w-sm mx-auto" data-id="element-3120">
          Klik op 'Valideer Nu' om de governance pipeline te starten en het
          vragenbestand te controleren.
        </p>
      </div>
    </div>;
}
export function FilterBar({
  moduleFilter,
  onModuleFilter,
  search,
  onSearch,
  children
}: {
  moduleFilter: string;
  onModuleFilter: (v: string) => void;
  search: string;
  onSearch: (v: string) => void;
  children?: React.ReactNode;
}) {
  const moduleOptions = [{
    value: 'all',
    label: 'Alle domeinen'
  }, ...Object.entries(MODULE_CONFIG).map(([code, config]) => ({
    value: code,
    label: `${config.name} (${code})`
  }))];
  return <div className="flex flex-col sm:flex-row gap-3 bg-white p-3 sm:p-4 rounded-xl border border-gray-200 shadow-sm" data-id="element-3121">
      <div className="flex-1 w-full sm:max-w-sm" data-id="element-3122">
        <Input type="text" placeholder="Zoek op ID of code..." value={search} onChange={e => onSearch(e.target.value)} icon={<Search className="w-4 h-4" data-id="element-3124" />} variant="admin" size="sm" data-id="element-3123" />
      </div>
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2" data-id="element-3125">
        <Select value={moduleFilter} onChange={e => onModuleFilter(e.target.value)} options={moduleOptions} variant="admin" size="sm" fullWidth={false} data-id="element-3126" />
        {children}
      </div>
    </div>;
}
export function IssueRow({
  issue
}: {
  issue: ValidationIssue;
}) {
  const sevConfig = SEVERITY_CONFIG[issue.kind];
  const Icon = sevConfig.icon;
  return <div className={`flex items-start gap-3 p-3 rounded-lg border ${sevConfig.bg} ${sevConfig.border}`} data-id="element-3127">
      <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${sevConfig.text}`} data-id="element-3128" />
      <div className="min-w-0 flex-1" data-id="element-3129">
        <div className="flex items-center gap-2 mb-1" data-id="element-3130">
          <span className={`text-xs font-bold uppercase ${sevConfig.text}`} data-id="element-3131">
            {sevConfig.label}
          </span>
          <span className="text-[10px] font-mono text-gray-500 bg-white/50 px-1.5 py-0.5 rounded border border-gray-200/50" data-id="element-3132">
            {issue.code}
          </span>
          <span className="text-[10px] text-gray-500 uppercase tracking-wider" data-id="element-3133">
            {issue.domain}
          </span>
        </div>
        <p className="text-sm text-gray-800 leading-snug" data-id="element-3134">{issue.message}</p>
        {issue.suggestion && <p className="text-xs text-gray-600 mt-1.5 flex items-start gap-1.5" data-id="element-3135">
            <span className="font-medium text-gray-700" data-id="element-3136">Suggestie:</span>
            {issue.suggestion}
          </p>}
      </div>
    </div>;
}
export function QuestionResultRow({
  questionId,
  verdict,
  score,
  issues,
  isExpanded,
  onToggle
}: {
  questionId: string;
  verdict: GateVerdict;
  score: number;
  issues: ValidationIssue[];
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const prefix = questionId.split('-')[0];
  const modConfig = MODULE_CONFIG[prefix] || {
    bgClass: 'bg-gray-500',
    textClass: 'text-gray-700'
  };
  const statusConfig = {
    PASS: {
      bg: 'bg-emerald-50',
      border: 'border-emerald-200',
      text: 'text-emerald-700',
      icon: CheckCircle2,
      label: 'Geslaagd'
    },
    REVIEW_NEEDED: {
      bg: 'bg-amber-50',
      border: 'border-amber-200',
      text: 'text-amber-700',
      icon: AlertTriangle,
      label: 'Review'
    },
    FAIL: {
      bg: 'bg-red-50',
      border: 'border-red-200',
      text: 'text-red-700',
      icon: XCircle,
      label: 'Fail'
    }
  }[verdict];
  const StatusIcon = statusConfig.icon;
  return <Card className={`overflow-hidden transition-colors border ${isExpanded ? 'border-blue-300 shadow-md' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-3137">
      <div className="px-4 py-3 flex items-center justify-between cursor-pointer bg-white" onClick={onToggle} data-id="element-3138">
        <div className="flex items-center gap-3 min-w-0" data-id="element-3139">
          <div className={`w-2 h-2 rounded-full flex-shrink-0 ${modConfig.bgClass}`} data-id="element-3140" />
          <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 min-w-0" data-id="element-3141">
            <span className="font-mono text-sm font-semibold text-gray-900 truncate" data-id="element-3142">
              {questionId}
            </span>
            <div className="flex items-center gap-2" data-id="element-3143">
              <Badge variant={verdict === 'PASS' ? 'success' : verdict === 'REVIEW_NEEDED' ? 'warning' : 'danger'} size="sm" data-id="element-3144">
                <StatusIcon className="w-3 h-3 mr-1 inline" data-id="element-3145" />
                {statusConfig.label}
              </Badge>
              {issues.length > 0 && <span className="text-xs text-gray-500" data-id="element-3146">
                  {issues.length} issue{issues.length > 1 ? 's' : ''}
                </span>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-4 flex-shrink-0" data-id="element-3147">
          <div className="hidden sm:flex items-center gap-2" data-id="element-3148">
            <div className="w-16 h-1.5 bg-gray-100 rounded-full overflow-hidden" data-id="element-3149">
              <div className={`h-full rounded-full ${score >= 80 ? 'bg-emerald-500' : score >= 60 ? 'bg-amber-500' : 'bg-red-500'}`} style={{
              width: `${score}%`
            }} data-id="element-3150" />
            </div>
            <span className="text-xs font-medium text-gray-600 w-8 text-right" data-id="element-3151">
              {score}%
            </span>
          </div>
          {isExpanded ? <ChevronDown className="w-4 h-4 text-gray-400" data-id="element-3152" /> : <ChevronRight className="w-4 h-4 text-gray-400" data-id="element-3153" />}
        </div>
      </div>
      {isExpanded && issues.length > 0 && <div className="px-4 py-3 bg-gray-50 border-t border-gray-100 space-y-2" data-id="element-3154">
          {issues.map((issue, idx) => <IssueRow key={idx} issue={issue} data-id="element-3155" />)}
        </div>}
      {isExpanded && issues.length === 0 && <div className="px-4 py-6 bg-gray-50 border-t border-gray-100 text-center" data-id="element-3156">
          <CheckCircle2 className="w-6 h-6 text-emerald-400 mx-auto mb-2" data-id="element-3157" />
          <p className="text-sm text-gray-600" data-id="element-3158">
            Geen issues gevonden. Deze vraag voldoet aan alle kwaliteitseisen.
          </p>
        </div>}
    </Card>;
}
// ─── Export Utilities ─────────────────────────────────────────────────────────
export function exportCSV(results: PipelineBatchResult['results'], filename: string) {
  const rows = [['Question ID', 'Verdict', 'Score', 'Issues Count', 'Blockers', 'Warnings']];
  results.forEach(r => {
    const blockers = r.gate.issues.filter(i => i.kind === 'blocker').length;
    const warnings = r.gate.issues.filter(i => i.kind === 'coach').length;
    rows.push([r.questionId, r.gate.verdict, r.gate.score.toString(), r.gate.issues.length.toString(), blockers.toString(), warnings.toString()]);
  });
  const csv = rows.map(r => r.join(',')).join('\n');
  const blob = new Blob([csv], {
    type: 'text/csv'
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${filename}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}
export function exportJSON(results: PipelineBatchResult['results'], filename: string, meta?: Record<string, unknown>) {
  const data = {
    timestamp: new Date().toISOString(),
    meta,
    results: results.map(r => ({
      questionId: r.questionId,
      verdict: r.gate.verdict,
      score: r.gate.score,
      issues: r.gate.issues.map(i => ({
        code: i.code,
        kind: i.kind,
        domain: i.domain,
        message: i.message
      }))
    }))
  };
  const blob = new Blob([JSON.stringify(data, null, 2)], {
    type: 'application/json'
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${filename}.json`;
  a.click();
  URL.revokeObjectURL(url);
}
export function exportMarkdown(results: PipelineBatchResult['results'], filename: string) {
  let md = `# Governance Validatie Rapport\n\nGenereerd op: ${new Date().toLocaleString('nl-NL')}\n\n`;
  const fails = results.filter(r => r.gate.verdict === 'FAIL');
  const reviews = results.filter(r => r.gate.verdict === 'REVIEW_NEEDED');
  md += `## Samenvatting\n- 🔴 FAIL: ${fails.length}\n- 🟡 REVIEW: ${reviews.length}\n- 🟢 PASS: ${results.length - fails.length - reviews.length}\n\n`;
  if (fails.length > 0) {
    md += `## 🔴 Geblokkeerde Vragen (FAIL)\n\n`;
    fails.forEach(r => {
      md += `### \`${r.questionId}\` (Score: ${r.gate.score}%)\n`;
      r.gate.issues.forEach(i => {
        const icon = i.kind === 'blocker' ? '❌' : i.kind === 'coach' ? '⚠️' : 'ℹ️';
        md += `- ${icon} **[${i.domain.toUpperCase()}]** ${i.message}\n`;
      });
      md += '\n';
    });
  }
  if (reviews.length > 0) {
    md += `## 🟡 Aandacht Vereist (REVIEW)\n\n`;
    reviews.forEach(r => {
      md += `### \`${r.questionId}\` (Score: ${r.gate.score}%)\n`;
      r.gate.issues.forEach(i => {
        const icon = i.kind === 'blocker' ? '❌' : i.kind === 'coach' ? '⚠️' : 'ℹ️';
        md += `- ${icon} **[${i.domain.toUpperCase()}]** ${i.message}\n`;
      });
      md += '\n';
    });
  }
  const blob = new Blob([md], {
    type: 'text/markdown'
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${filename}.md`;
  a.click();
  URL.revokeObjectURL(url);
}
export function ExportMenu({
  results,
  filePrefix,
  meta
}: {
  results: PipelineBatchResult['results'];
  filePrefix: string;
  meta?: Record<string, unknown>;
}) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  useClickOutside(menuRef, () => setOpen(false));
  const dateStr = new Date().toISOString().split('T')[0];
  const filename = `${filePrefix}-${dateStr}`;
  return <div className="relative" ref={menuRef} data-id="element-3159">
      <button onClick={() => setOpen(!open)} className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors shadow-sm" data-id="element-3160">
        <Download className="w-4 h-4" data-id="element-3161" />
        <span className="hidden sm:inline" data-id="element-3162">Exporteer</span>
        <ChevronDown className="w-3.5 h-3.5 ml-0.5 text-gray-400" data-id="element-3163" />
      </button>
      {open && <div className="absolute right-0 mt-1 w-48 bg-white rounded-xl shadow-lg border border-gray-200 py-1 z-20" data-id="element-3164">
          <button onClick={() => {
        exportCSV(results, filename);
        setOpen(false);
      }} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-blue-600 transition-colors" data-id="element-3165">
            Exporteer als CSV
          </button>
          <button onClick={() => {
        exportJSON(results, filename, meta);
        setOpen(false);
      }} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-blue-600 transition-colors" data-id="element-3166">
            Exporteer als JSON
          </button>
          <button onClick={() => {
        exportMarkdown(results, filename);
        setOpen(false);
      }} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-blue-600 transition-colors" data-id="element-3167">
            Exporteer als Markdown
          </button>
        </div>}
    </div>;
}